import React, { useCallback, useEffect, useState } from 'react';

import {
  FollowUpBlock,
  GuardianFollowUp,
  SentFollowUp,
  TeacherFollowUpView,
  getGuardianFollowUps,
  getTeacherFollowUp,
  sendFollowUp,
} from '../api/absence-follow-up';

/**
 * MC-04 v1-2 — the optional follow-up after a learner missed a class.
 *
 * The teacher chooses an approved message and sees exactly what the family
 * will read; there is no field to write or edit it (MC04-R13), and the server
 * refuses one anyway. It goes to the guardian, never to the child (R04), and
 * it changes nothing about attendance, payment or closing the class (R03).
 */

const BLOCK_TEXT: Record<FollowUpBlock, string> = {
  NOT_A_SESSION: 'El seguimiento solo aplica a una sesión de clase.',
  SESSION_NOT_DELIVERED: 'Esta sesión no se impartió, así que no hay seguimiento que enviar.',
  SESSION_NOT_ENDED: 'Podrás enviar un seguimiento cuando la sesión termine.',
  ATTENDANCE_PENDING:
    'La asistencia del alumno aún no está determinada. No enviamos un mensaje que diga que faltó hasta saberlo.',
  LEARNER_ATTENDED: 'La videollamada registró al alumno en esta sesión.',
};

const EMAIL_TEXT: Record<SentFollowUp['emailStatus'], string> = {
  SENT: 'La familia lo recibió en IQMaxx y en su correo.',
  PENDING: 'La familia lo tiene en IQMaxx. El correo todavía no salió.',
  FAILED: 'La familia lo tiene en IQMaxx, pero no pudimos enviar el correo.',
};

const box: React.CSSProperties = {
  border: '1px solid #d1d5da',
  borderRadius: 8,
  padding: 16,
  marginBottom: 16,
};
const muted: React.CSSProperties = { color: '#666', fontSize: 14, marginTop: 0 };
const quote: React.CSSProperties = {
  margin: '8px 0 12px',
  padding: '10px 12px',
  background: '#f8f9fa',
  borderLeft: '3px solid #adb5bd',
  fontSize: 14,
};
const button = (disabled: boolean): React.CSSProperties => ({
  padding: '8px 14px',
  border: 'none',
  borderRadius: 6,
  background: disabled ? '#adb5bd' : '#1971c2',
  color: '#fff',
  fontSize: 14,
  cursor: disabled ? 'not-allowed' : 'pointer',
});

/** The teacher's side, on SessionEvidence for a learner marked NO_SHOW by the video call. */
export const AbsenceFollowUpPanel: React.FC<{ enrollmentId: string; sequence: number }> = ({
  enrollmentId,
  sequence,
}) => {
  const [view, setView] = useState<TeacherFollowUpView | null>(null);
  const [chosen, setChosen] = useState<string>('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);

  const load = useCallback(async () => {
    try {
      const v = await getTeacherFollowUp(enrollmentId, sequence);
      setView(v);
      setLoadFailed(false);
      setChosen((c) => c || v.options[0]?.messageId || '');
    } catch {
      setLoadFailed(true);
    }
  }, [enrollmentId, sequence]);

  useEffect(() => {
    void load();
  }, [load]);

  const send = async (messageId: string) => {
    setBusy(true);
    setError(null);
    try {
      await sendFollowUp(enrollmentId, sequence, messageId);
      await load();
    } catch (e: any) {
      setError(e?.message || 'No pudimos enviar el seguimiento. Inténtalo de nuevo.');
    } finally {
      setBusy(false);
    }
  };

  const sent = view?.sent ?? null;

  return (
    <section style={box} aria-labelledby="absence-follow-up-title">
      <h2 id="absence-follow-up-title" style={{ fontSize: 16, margin: '0 0 12px' }}>
        Seguimiento al alumno ausente
      </h2>
      <p style={muted}>
        Es opcional. No cambia la asistencia, el pago ni el cierre de la clase. Lo recibe el padre,
        madre o tutor, no el alumno.
      </p>

      {loadFailed && (
        <p role="alert" style={{ color: '#c92a2a', fontSize: 14 }}>
          No pudimos cargar el seguimiento de esta sesión.
        </p>
      )}

      {sent && (
        <div role="status">
          <p style={{ margin: 0, fontSize: 14 }}>
            <strong>Seguimiento enviado.</strong> {EMAIL_TEXT[sent.emailStatus]}
          </p>
          <blockquote style={quote}>{sent.body}</blockquote>
          {sent.emailStatus !== 'SENT' && (
            <button disabled={busy} style={button(busy)} onClick={() => void send(sent.messageId)}>
              Reintentar el correo
            </button>
          )}
        </div>
      )}

      {view && !sent && view.block && <p style={muted}>{BLOCK_TEXT[view.block]}</p>}

      {view && !sent && !view.block && view.options.length > 0 && (
        <>
          <fieldset style={{ border: 'none', padding: 0, margin: 0 }}>
            <legend style={{ fontSize: 14, marginBottom: 8 }}>Mensaje aprobado</legend>
            {view.options.map((o) => (
              <div key={o.messageId}>
                <label style={{ fontSize: 14 }}>
                  <input
                    type="radio"
                    name="follow-up-message"
                    value={o.messageId}
                    checked={chosen === o.messageId}
                    onChange={() => setChosen(o.messageId)}
                  />{' '}
                  {o.label}
                </label>
                <blockquote style={quote}>{o.preview}</blockquote>
              </div>
            ))}
          </fieldset>
          <p style={muted}>Para: {view.recipient}. El texto está aprobado y no se puede editar.</p>
          <button
            disabled={busy || !chosen}
            style={button(busy || !chosen)}
            onClick={() => void send(chosen)}
          >
            Enviar seguimiento
          </button>
        </>
      )}

      {error && (
        <p role="alert" style={{ color: '#c92a2a', fontSize: 14 }}>
          {error}
        </p>
      )}
    </section>
  );
};

/** The guardian's side: the teacher's follow-up for this session, if one was sent. */
export const GuardianFollowUpNote: React.FC<{ enrollmentId: string; sequence: number }> = ({
  enrollmentId,
  sequence,
}) => {
  const [note, setNote] = useState<GuardianFollowUp | null>(null);

  useEffect(() => {
    let live = true;
    getGuardianFollowUps(enrollmentId)
      .then((res) => {
        if (live) setNote(res.followUps.find((f) => f.sequence === sequence) ?? null);
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [enrollmentId, sequence]);

  if (!note) return null;
  return (
    <section style={box} aria-labelledby="guardian-follow-up-title">
      <h2 id="guardian-follow-up-title" style={{ fontSize: 16, margin: '0 0 12px' }}>
        Mensaje del profesor
      </h2>
      <blockquote style={quote}>{note.body}</blockquote>
      <p style={muted}>
        Es un mensaje informativo y no cambia nada de la clase. Si hubo un problema para entrar,
        puedes contarlo desde Ayuda.
      </p>
    </section>
  );
};
